import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DocumentsAPI } from "../api/documents";
import type { Document } from "../types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";

// Query Keys
export const documentKeys = {
  all: ["documents"] as const,
  lists: () => [...documentKeys.all, "list"] as const,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  list: (clientId: string, filters?: Record<string, any>) =>
    [...documentKeys.lists(), clientId, filters] as const,
  details: () => [...documentKeys.all, "detail"] as const,
  detail: (clientId: string, documentId: string) =>
    [...documentKeys.details(), clientId, documentId] as const,
};

// List Documents Query
export function useDocuments(options?: {
  status?: string;
  limit?: number;
  offset?: number;
}) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<{ documents: Document[]; total: number }>({
    queryKey: documentKeys.list(clientId, options),
    queryFn: () => DocumentsAPI.listDocuments(clientId, options),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!clientId,
  });
}

// Get Document Query
export function useDocument(documentId: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<Document>({
    queryKey: documentKeys.detail(clientId, documentId),
    queryFn: () => DocumentsAPI.getDocument(clientId, documentId),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!clientId && !!documentId,
  });
}

// Upload Documents Mutation
export function useUploadDocuments() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (files: File[]) =>
      DocumentsAPI.uploadDocuments(clientId, files),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.lists(),
      });
    },
  });
}

// Delete Document Mutation
export function useDeleteDocument() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      DocumentsAPI.deleteDocument(clientId, documentId),
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: documentKeys.detail(clientId, documentId),
      });
    },
  });
}

// Re-embed Document Mutation
export function useReEmbedDocument() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      DocumentsAPI.reEmbedDocument(clientId, documentId),
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.detail(clientId, documentId),
      });
      queryClient.invalidateQueries({
        queryKey: documentKeys.lists(),
      });
    },
  });
}

// Search Repository Mutation
export function useSearchRepository() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useMutation({
    mutationFn: ({
      query,
      options,
    }: {
      query: string;
      options?: { limit?: number; threshold?: number };
    }) => DocumentsAPI.searchRepository(clientId, query, options),
  });
}
